import React, { useEffect, useRef, useState } from 'react';
import { apiClient } from '../../api';
import { errMessage } from '../UI/ink';

// Delete with real counts: what goes (machine data) and what stays, marked
// "asset deleted" (findings, case links, case evidence, runs, action history).
// Typing the hostname enables the button; Archive is offered instead.
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export default function DeleteAssetDialog({ asset, onClose, onDeleted, onArchive }) {
  const [impact, setImpact] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    apiClient.get(`/api/servers/${asset.id}/delete-impact`).then(r => setImpact(r.data)).catch(() => setImpact(false));
    inputRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [asset.id, onClose]);

  const ok = typed.trim() === asset.name;
  const del = async () => {
    setBusy(true); setError(null);
    try {
      await apiClient.delete(`/api/servers/${asset.id}`);
      onDeleted();
    } catch (err) {
      setError(errMessage(err, 'Could not delete the asset.'));
      setBusy(false);
    }
  };

  const d = impact?.deleted;
  const k = impact?.kept;
  const goes = d ? [
    d.snapshots ? `${plural(d.snapshots, 'snapshot')} and ${plural(d.packages, 'package record')}` : 'No snapshots',
    d.version_changes ? plural(d.version_changes, 'version change') : null,
    (d.ioc_alerts || d.cve_alerts) ? `${plural(d.ioc_alerts, 'IoC alert')} and ${plural(d.cve_alerts, 'CVE alert')} not linked to a case` : null,
    d.baseline ? 'Baseline' : null,
    impact.cancelled?.pending_response_actions ? `${plural(impact.cancelled.pending_response_actions, 'pending response action')} (cancelled, record kept)` : null,
  ].filter(Boolean) : [];
  const stays = k ? [
    k.findings ? plural(k.findings, 'finding') : null,
    k.cases ? `Links to ${plural(k.cases, 'case')}` : null,
    (k.ioc_alerts_linked_to_cases || k.cve_alerts_linked_to_cases) ? `${plural(k.ioc_alerts_linked_to_cases + k.cve_alerts_linked_to_cases, 'alert')} used as case evidence` : null,
    k.playbook_runs ? plural(k.playbook_runs, 'playbook run') : null,
    k.response_action_history ? plural(k.response_action_history, 'past response action') : null,
  ].filter(Boolean) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[18vh]" style={{ background: 'rgba(0,0,0,0.6)' }} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="del-title" className="w-[520px] max-w-full box-border flex flex-col gap-3.5 px-6 py-[22px] rounded-[14px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }}>
        <h2 id="del-title" className="m-0 font-heading text-[19px] font-semibold break-words">Delete {asset.name} permanently?</h2>
        {impact === null && <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>Counting what this affects…</span>}
        {impact === false && <span className="text-[13px]" style={{ color: 'var(--sev-crit-fg)' }}>Couldn’t count what this affects. You can still archive it instead.</span>}
        {impact && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-[5px] p-3 rounded-[10px]" style={{ background: 'var(--sev-crit-bg)' }}>
              <span className="font-mono text-[10px] tracking-[0.12em]">DELETED</span>
              <span className="text-[12.5px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>{goes.map(t => <span key={t} className="block">{t}</span>)}</span>
            </div>
            <div className="flex flex-col gap-[5px] p-3 rounded-[10px]" style={{ background: 'var(--surf-raised)' }}>
              <span className="font-mono text-[10px] tracking-[0.12em]">KEPT, MARKED “ASSET DELETED”</span>
              <span className="text-[12.5px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>{stays.length ? stays.map(t => <span key={t} className="block">{t}</span>) : 'Nothing else refers to it'}</span>
            </div>
          </div>
        )}
        <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>This can’t be undone. If the machine is just being retired, archive it instead and keep its history.</span>
        <label className="flex flex-col gap-1.5 text-[13px]">
          <span>Type <span className="font-mono">{asset.name}</span> to confirm</span>
          <input ref={inputRef} value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" spellCheck={false} className="h-[42px] box-border px-3 rounded-[9px] border font-mono text-[13.5px] outline-none focus:border-[color:var(--sev-crit-fg)]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
        </label>
        {error && <span role="alert" className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={!ok || busy} onClick={del} className="h-11 px-4 rounded-[10px] text-sm font-semibold disabled:opacity-45" style={{ background: 'var(--sev-crit-fg)', color: '#0A0A0A' }}>{busy ? 'Deleting…' : 'Delete permanently'}</button>
          <button type="button" onClick={onArchive} className="h-11 px-3.5 rounded-[10px] border text-sm font-medium" style={{ borderColor: 'var(--surf-line-strong)' }}>Archive instead</button>
          <button type="button" onClick={onClose} className="h-11 px-2.5 text-sm" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
